/**
 * Transport-drop recovery policy (#478).
 *
 * A phone that backgrounds the game loses its WebSocket/ICE; livekit gives up
 * after its own retries and the SDK emits `disconnected` and returns to idle.
 * The server side is built for this — the session token stays valid and
 * `/connect` recreates the room lazily — but nothing on the client called
 * `connect()` again, so the dealer stayed dead until a reload.
 *
 * This scheduler owns the decision and the pacing; the host supplies the
 * `connect()`, the page-activity predicate and the timers, which keeps the
 * logic pure and unit-testable.
 *
 * - Reasons that mean "stay down": our own disconnect, and a duplicate
 *   identity (another tab took the session — reconnecting would fight it).
 * - Attempts run only while the page is visible and online; a hidden page
 *   arms and waits for `onPageActive()`.
 * - Backoff 1/2/4/8/8 s, five attempts, then `onGaveUp` (the host shows its
 *   retry chrome — a full reload). `requestNow()` skips the wait and always
 *   gets one attempt, even after giving up — it is the seam for a
 *   gesture-retry affordance (filed follow-up); nothing wires it yet.
 * - Terminal verdicts (`isTerminalError` — the server closed the session):
 *   every failure walks the full ladder; the ending is quiet (`onTerminal`,
 *   no retry chrome) if a terminal verdict was seen at any point during the
 *   episode, otherwise the retry chrome (`onGaveUp`).
 *
 *   DELIBERATE BIAS (desk ruling 2026-08-31, five review rounds on record):
 *   the server cannot yet distinguish "session closed" from "session store
 *   failed" (#485), so EVERY client rule here mis-ends one rare case — this
 *   shape prefers a quiet ending over retry chrome because intentional closes
 *   are common (operator expiry) while the losing case needs a store failure
 *   AND a flapping radio inside one 23 s episode, and a reload recovers it.
 *   Do not add corroboration rules — they only rotate the losing case; the
 *   fix is #485, which deletes `isTerminalError`'s ambiguity entirely.
 */

export const STAY_DOWN_REASONS: ReadonlySet<string> = new Set([
  'CLIENT_INITIATED',
  'DUPLICATE_IDENTITY',
]);

export const RECONNECT_BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000, 8000];

export function shouldReconnect(reason: string | undefined): boolean {
  return reason === undefined || !STAY_DOWN_REASONS.has(reason);
}

export interface ReconnectSchedulerDeps {
  connect(): Promise<void>;
  /** Visible and online — the only state in which an attempt can succeed. */
  isPageActive(): boolean;
  onReconnected(): void;
  onGaveUp(error: unknown): void;
  /** A verdict no retry can change (the server closed the session). Never
   * shortcuts the ladder; it makes the eventual give-up quiet — `onTerminal`
   * instead of `onGaveUp`. */
  isTerminalError?(error: unknown): boolean;
  /** Logging-only observer for a quiet (terminal-verdict) give-up. */
  onTerminal?(error: unknown): void;
  /** Injectable for tests; default to the globals. */
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export interface ReconnectSchedulerState {
  /** A drop was seen and recovery has not succeeded (or been abandoned) yet. */
  armed: boolean;
  /** Attempts made since the drop. */
  attempts: number;
  /** A connect() is in flight. */
  inFlight: boolean;
}

export interface ReconnectScheduler {
  onDisconnected(reason?: string): void;
  /** visibilitychange → visible, `online`, pageshow — re-evaluate a waiting drop. */
  onPageActive(): void;
  /** Attempt immediately (dedupes onto an in-flight connect). Unwired today —
   * the entry point a gesture-retry affordance (filed follow-up) would call. */
  requestNow(): Promise<void>;
  getState(): ReconnectSchedulerState;
  destroy(): void;
}

export function createReconnectScheduler(deps: ReconnectSchedulerDeps): ReconnectScheduler {
  const setTimer = deps.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimeout ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));

  let armed = false;
  let attempts = 0;
  let sawTerminal = false;
  let timer: unknown = null;
  let inFlight: Promise<void> | null = null;
  let destroyed = false;

  const cancelTimer = (): void => {
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  };

  const attempt = (): Promise<void> => {
    if (inFlight) return inFlight;
    attempts += 1;
    // `inFlight` is cleared inside the handlers, before any follow-up is
    // scheduled — a `.finally` would run after `schedule()` and block it.
    inFlight = deps.connect().then(
      () => {
        inFlight = null;
        if (destroyed) return;
        armed = false;
        attempts = 0;
        sawTerminal = false;
        deps.onReconnected();
      },
      (error: unknown) => {
        inFlight = null;
        if (destroyed) return;
        if (deps.isTerminalError?.(error)) sawTerminal = true;
        if (!armed) return; // a requestNow() retry after giving up: surface nothing new
        if (attempts >= RECONNECT_BACKOFF_MS.length) {
          armed = false;
          if (sawTerminal) deps.onTerminal?.(error);
          else deps.onGaveUp(error);
          return;
        }
        schedule();
      }
    );
    return inFlight;
  };

  const schedule = (): void => {
    if (destroyed || !armed || timer !== null || inFlight) return;
    if (!deps.isPageActive()) return; // wait for onPageActive
    const delay = RECONNECT_BACKOFF_MS[Math.min(attempts, RECONNECT_BACKOFF_MS.length - 1)];
    timer = setTimer(() => {
      timer = null;
      void attempt();
    }, delay);
  };

  return {
    onDisconnected(reason?: string): void {
      if (destroyed || !shouldReconnect(reason)) return;
      if (armed) return; // already recovering
      armed = true;
      attempts = 0;
      sawTerminal = false;
      schedule();
    },

    onPageActive(): void {
      schedule();
    },

    requestNow(): Promise<void> {
      if (destroyed) return Promise.resolve();
      cancelTimer();
      return attempt();
    },

    getState(): ReconnectSchedulerState {
      return { armed, attempts, inFlight: inFlight !== null };
    },

    destroy(): void {
      destroyed = true;
      armed = false;
      cancelTimer();
    },
  };
}
